import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import downloadIcon from '../assets/Icon/download_icon.svg';
import { apiService } from '../services/apiService';
import * as XLSX from 'xlsx';
import notificationService from '../CommanService/notificationService';
import './BatchPricingOverride.css'; // Import the CSS file for styling
import Multiselect from 'multiselect-react-dropdown';
import { hasPermission, PERMISSION_CODES, filterDataByUserAccess } from '../utils/permissionUtils';
import searchIcon from '../assets/Icon/search_icon.svg';
import dropdownArrow from '../assets/Icon/dropdown_arrow.svg';

const NewCom = () => {
  const { user } = useSelector(state => state.auth);
  const [finalisedData, setFinalisedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [originalData, setOriginalData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  const [regions, setRegions] = useState([]);
  const [interchangeNumbers, setInterchangeNumbers] = useState([]);
  const [partTypes, setPartTypes] = useState([]);

  // Selected values state
  const [selectedRegions, setSelectedRegions] = useState(['All']);
  const [selectedPartTypes, setSelectedPartTypes] = useState(['All']);
  const [selectedInterchangeNumbers, setSelectedInterchangeNumbers] = useState(['All']);

  const [comparisonOperator, setComparisonOperator] = useState('greater');
  const [comparisonValue, setComparisonValue] = useState('');

 const [appliedFilters, setAppliedFilters] = useState({
    partTypes: ['All'],
    regions: ['All'],
    interchangeNumbers: ['All'],
    comparisonOperator: '',
    comparisonValue: ''
  });




  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };











  const handleDownloadExcel = () => {
    const dataToExport = finalisedData;
    if (dataToExport.length === 0) {
      notificationService.showWarning('No data to export');
      return;
    }
    const excelData = dataToExport.map(item => {
      const originalPrice = parseFloat(item.original_price) || 0;
      const adjustedPrice = parseFloat(item.adjusted_price) || 0;
      const percentageDifference = originalPrice > 0
        ? ((adjustedPrice - originalPrice) / originalPrice * 100).toFixed(2)
        : '0.00';
      return {
        'Part Name': item.part_name || '',
        'Region': item.region || '',
        'IC Number': item.interchange_number || '',
        'Mileage': item.mileage || 0,
        'Original Price': originalPrice,
        'Adjusted Price': adjustedPrice,
        '% Difference': percentageDifference + '%',
        'Finalized By': item.approved_by_name || ''
      };
    });
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Impact Analysis Data');
    const today = new Date();
    const dateString = today.toISOString().split('T')[0];
    const fileName = `Impact_Analysis_Report_${dateString}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };


 // Fetch finalised data on component mount
  useEffect(() => {
    const fetchFinalisedData = async () => {
      try {
        setLoading(true);
        const response = await apiService.GetFinalisedData();
        if (response && response.data) {
          setFinalisedData(response.data);
          setOriginalData([...response.data]); // Store original data for comparison
          
          // Extract unique values for dropdowns and add "All" option
          const uniqueRegions = ['All', ...new Set(response.data.map(item => item.region).filter(Boolean))];
          const uniqueInterchanges = ['All', ...new Set(response.data.map(item => item.interchange_number).filter(Boolean))];
          const uniquePartName=['All', ...new Set(response.data.map(item => item.part_name).filter(Boolean))]
          setRegions(uniqueRegions);
          setInterchangeNumbers(uniqueInterchanges);
          
          setPartTypes(uniquePartName);
    // Always start with "All" for finalyzer to avoid conflicts
          
        }
      } catch (error) {
        console.error('Error fetching finalised data:', error);
        // Set default values on error
        setRegions(['All', 'MAR', 'NAR', 'EUR']);
        setInterchangeNumbers(['All', '10007', '10008', '10009']);
      
        setPartTypes(['All', 'ENGINE', 'TRANSMISSION']);
        
                 // Always start with "All" for finalyzer to avoid conflicts
      
      } finally {
        setLoading(false);
      }
    };

    fetchFinalisedData();
  }, []);



    const updateDropdownsBasedOnPartTypes = (selectedPartTypes) => {
    if (!finalisedData.length) return;

    console.log('Updating dropdowns based on Part Types:', selectedPartTypes);

    if (selectedPartTypes.includes('All')) {
      // If "All" is selected, show all regions and IC numbers
      const uniqueRegions = ['All', ...new Set(finalisedData.map(item => item.region).filter(Boolean))];
      const uniqueInterchanges = ['All', ...new Set(finalisedData.map(item => item.interchange_number).filter(Boolean))];
      setRegions(uniqueRegions);
      setInterchangeNumbers(uniqueInterchanges);
      console.log('Updated dropdowns for "All" - Regions:', uniqueRegions, 'IC Numbers:', uniqueInterchanges);
    } else {
      // Filter data by ALL selected part types (not just the first one)
      const filteredByPartTypes = finalisedData.filter(item => 
        selectedPartTypes.some(partType => item.part_name === partType)
      );
      console.log(`Filtered data for Part Types [${selectedPartTypes.join(', ')}]:`, filteredByPartTypes.length, 'items');
      
      // Extract unique regions and IC numbers from filtered data
      const uniqueRegions = ['All', ...new Set(filteredByPartTypes.map(item => item.region).filter(Boolean))];
      const uniqueInterchanges = ['All', ...new Set(filteredByPartTypes.map(item => item.interchange_number).filter(Boolean))];
      
      setRegions(uniqueRegions);
      setInterchangeNumbers(uniqueInterchanges);
      console.log(`Updated dropdowns for Part Types [${selectedPartTypes.join(', ')}] - Regions:`, uniqueRegions, 'IC Numbers:', uniqueInterchanges);
      
      // Log detailed breakdown of regions and IC numbers per part type
      selectedPartTypes.forEach(partType => {
        const partTypeData = finalisedData.filter(item => item.part_name === partType);
        const partTypeRegions = [...new Set(partTypeData.map(item => item.region).filter(Boolean))];
        const partTypeICNumbers = [...new Set(partTypeData.map(item => item.interchange_number).filter(Boolean))];
        console.log(`Part Type "${partType}":`, {
          count: partTypeData.length,
          regions: partTypeRegions,
          icNumbers: partTypeICNumbers
        });
      });      
      // Don't reset selections automatically - let user choose
    }
  };



    const updateICNumbersBasedOnRegion = (selectedRegions) => {
    if (!finalisedData.length) return;

    console.log('Updating IC Numbers based on Regions:', selectedRegions);

    if (selectedRegions.includes('All')) {
      // If "All" is selected, show IC numbers based on current part types filter
      let filteredData = finalisedData;
      
      if (!selectedPartTypes.includes('All')) {
        filteredData = finalisedData.filter(item => 
          selectedPartTypes.some(partType => item.part_name === partType)
        );
      }
      
      const uniqueInterchanges = ['All', ...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))];
      setInterchangeNumbers(uniqueInterchanges);
      console.log('Updated IC Numbers for "All" regions:', uniqueInterchanges);
    } else {
      // Filter data by selected regions and part types
      let filteredData = finalisedData.filter(item => 
        selectedRegions.some(region => item.region === region)
      );
      
      if (!selectedPartTypes.includes('All')) {
        filteredData = filteredData.filter(item => 
          selectedPartTypes.some(partType => item.part_name === partType)
        );
      }
      
      const uniqueInterchanges = ['All', ...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))];
      setInterchangeNumbers(uniqueInterchanges);
      console.log(`Updated IC Numbers for Regions [${selectedRegions.join(', ')}]:`, uniqueInterchanges);
      
      // Don't reset IC number selection automatically - let user choose
    }
  };

  // Handle dropdown selection with "All" logic
  const handleSelect = (selectedList, selectedItem, setSelectedValues) => {
    console.log('Selected:', selectedList);
    
    // If "All" is selected, clear other selections
    if (selectedItem === 'All') {
      setSelectedValues(['All']);
      
      // Update dropdowns when "All" is selected
      if (setSelectedValues === setSelectedPartTypes) {
        updateDropdownsBasedOnPartTypes(['All']);
      } else if (setSelectedValues === setSelectedRegions) {
        updateICNumbersBasedOnRegion(['All']);
      }
    } else {
      // If a specific item is selected, remove "All" from the list
      const filteredList = selectedList.filter(item => item !== 'All');
      setSelectedValues(filteredList);
      
      // Update dropdowns when specific items are selected
      if (setSelectedValues === setSelectedPartTypes) {
        updateDropdownsBasedOnPartTypes(filteredList);
      } else if (setSelectedValues === setSelectedRegions) {
        updateICNumbersBasedOnRegion(filteredList);
      }
    }
  };


    const handleRemove = (selectedList, removedItem, setSelectedValues, options) => {
    console.log('Removed:', selectedList);
    
    // If "All" is being removed, do nothing (keep "All" selected)
    if (removedItem === 'All') {
      return;
    }
    
    // If no items are left after removal, select "All"
    if (selectedList.length === 0) {
      setSelectedValues(['All']);
      
      // Update dropdowns when "All" is selected
      if (setSelectedValues === setSelectedPartTypes) {
        updateDropdownsBasedOnPartTypes(['All']);
      } else if (setSelectedValues === setSelectedRegions) {
        updateICNumbersBasedOnRegion(['All']);
      }
    } else {
      setSelectedValues(selectedList);
      
      // Update dropdowns based on remaining selections
      if (setSelectedValues === setSelectedPartTypes && selectedList.length > 0) {
        updateDropdownsBasedOnPartTypes(selectedList);
      } else if (setSelectedValues === setSelectedRegions && selectedList.length > 0) {
        updateICNumbersBasedOnRegion(selectedList);
      }
    }
  };

  const handleSearch = () => {
    console.log('Search button clicked. Current filters:', {
      regions: selectedRegions,
      partTypes: selectedPartTypes,
      interchangeNumbers: selectedInterchangeNumbers,
      comparisonOperator: comparisonOperator,
      comparisonValue: comparisonValue
    });
    
    // Apply the current selections as filters
    setAppliedFilters({
      partTypes: selectedPartTypes,
      regions: selectedRegions,
      interchangeNumbers: selectedInterchangeNumbers,
      comparisonOperator: comparisonOperator,
      comparisonValue: comparisonValue
    });
    
    // Reset to first page when applying new filters
    setCurrentPage(1);
  };
 useEffect(() => {
    // This will trigger a re-render with updated statistics
    // when applied filters change (after search button click)
  }, [appliedFilters]);



  const getCurrentFilteredData = () => {
    let filteredData = finalisedData;
    
    // Check if any filters are applied
    const hasFilters = !appliedFilters.partTypes.includes('All') || 
                      !appliedFilters.regions.includes('All') || 
                      !appliedFilters.interchangeNumbers.includes('All') ||
                      appliedFilters.comparisonValue !== '';
    
    // Only apply filters if search button has been clicked and filters are set
    if (hasFilters) {
      
      // Filter by part type
      if (!appliedFilters.partTypes.includes('All')) {
        filteredData = filteredData.filter(item => 
          appliedFilters.partTypes.some(partType => item.part_name === partType)
        );
      }
      
      // Filter by region
      if (!appliedFilters.regions.includes('All')) {
        filteredData = filteredData.filter(item => 
          appliedFilters.regions.some(region => item.region === region)
        );
      }
      
      // Filter by IC number
      if (!appliedFilters.interchangeNumbers.includes('All')) {
        filteredData = filteredData.filter(item => 
          appliedFilters.interchangeNumbers.some(icNumber => item.interchange_number === icNumber)
        );
      }
      
      // Filter by percentage difference - this should work independently
      if (appliedFilters.comparisonValue !== '') {
        filteredData = filteredData.filter(item => {
          const originalPrice = parseFloat(item.original_price) || 0;
          const adjustedPrice = parseFloat(item.adjusted_price) || 0;
          const percentDiff = originalPrice > 0 
            ? ((adjustedPrice - originalPrice) / originalPrice) * 100
            : 0;

          if (appliedFilters.comparisonOperator === 'greater') {
            return percentDiff > Number(appliedFilters.comparisonValue);
          }
          if (appliedFilters.comparisonOperator === 'less') {
            return percentDiff < Number(appliedFilters.comparisonValue);
          }
          return true;
        });
      }
    }
    
    // Apply user region access filtering
    const userAccessFiltered = filteredData.filter(item => {
      const userRegionAccess = filterDataByUserAccess([item], user, 'region');
      return userRegionAccess.length > 0;
    });
    
    return userAccessFiltered;
  };

  const filteredData = getCurrentFilteredData();





//   const handlePriceChange = async (id, newValue) => {
//     try {
//       const numericValue = parseFloat(newValue);
      
//       // Validate input
//       if (isNaN(numericValue) || numericValue < 0) {
//         notificationService.showError('Please enter a valid positive number');
//         return;
//       }
      
//       // Update the local state
//       const updatedData = finalisedData.map(item => 
//         item.id === id 
//           ? { ...item, adjusted_price: numericValue }
//           : item
//       );
//       setFinalisedData(updatedData);
      
//       // Check if there are unsaved changes
//       const hasUnsaved = updatedData.some((item, index) => {
//         const original = originalData[index];
//         return original && original.adjusted_price !== item.adjusted_price;
//       });
//       setHasChanges(hasUnsaved);
      
//       // Call the batchexpertqueue controller to update the batch
//       try {
//         const updatedItem = updatedData.find(item => item.id === id);
       
//         if (updatedItem) {
//           //  Prepare the batch data for the API call
//            const batchData = {
//            id: updatedItem.id,
//             adjusted_price: updatedItem.adjusted_price,
       
//            };

       
//  await apiService.UpdateBatch([batchData]);
          
//           // Call the UpdateBatch API method from batchexpertqueue controller
         

          
//           // Log success for debugging (only in development)
//           if (process.env.NODE_ENV === 'development') {
//             console.log('Successfully updated batch expert queue for item:', updatedItem.id);
//           }
//         }
//       } catch (batchError) {
//         console.error('Error updating batch expert queue:', batchError);
//         // Don't show error to user for batch queue update - it's a background process
//         // The main price change functionality should still work
//       }
      
//     } catch (error) {
//       console.error('Error updating adjusted price:', error);
//       notificationService.showError('Failed to update adjusted price');
//     }
//   };

  //checks any one value is changed or not otherwise return false
  const handlePriceChange = async (id, newValue) => {
  try {
    const numericValue = parseFloat(newValue);

    // Validate input
    if (isNaN(numericValue) || numericValue < 0) {
      notificationService.showError('Please enter a valid positive number');
      return;
    }

    // Find the original item to compare
    const originalItem = originalData.find(item => item.id === id);
    if (!originalItem) return;

    // Convert both values to numbers for proper comparison
    const originalPrice = parseFloat(originalItem.adjusted_price);
    const newPrice = parseFloat(numericValue);

    // Only update if the value actually changed
    if (originalPrice === newPrice) {
      console.log('No change detected:', originalPrice, '===', newPrice);
      return; // No change, don't update anything
    }

    console.log('Change detected:', originalPrice, '!==', newPrice);

    // Update local state only, do not call API here
    const updatedData = finalisedData.map(item =>
      item.id === id ? { ...item, adjusted_price: numericValue } : item
    );
    setFinalisedData(updatedData);

    // Check for unsaved changes
    const hasUnsaved = updatedData.some((item, index) => {
      const original = originalData[index];
      return original && original.adjusted_price !== item.adjusted_price;
    });
    setHasChanges(hasUnsaved);
  } catch (error) {
    console.error('Error updating price:', error);
    notificationService.showError('Failed to update adjusted price');
  }
};





  
  const hasUnsavedChanges = () => {
    if (originalData.length === 0 || finalisedData.length === 0) return false;
    
    return finalisedData.some((item, index) => {
      const original = originalData[index];
      return original && original.adjusted_price !== item.adjusted_price;
    });
  };











  //Returns the number of element changed
  const getUnsavedChangesCount = () => {
    if (originalData.length === 0 || finalisedData.length === 0) return 0;
    
    return finalisedData.filter((item, index) => {
      const original = originalData[index];
      return original && original.adjusted_price !== item.adjusted_price;
    }).length;
  };






  const handleSaveAllChanges = async () => {
    try {
      if (!hasUnsavedChanges()) {
        notificationService.showInfo('No changes to save');
        return;
      }

      // Find all items that have been changed
      const changedItems = finalisedData.filter((item, index) => {
        const original = originalData[index];
        return original && original.adjusted_price !== item.adjusted_price;
      });

      if (changedItems.length === 0) {
        notificationService.showInfo('No changes to save');
        return;
      }
             
  // Show confirmation dialog
  // const confirmMessage = `Are you sure you want to save ${changedItems.length} changes?\n\nThis will update the adjusted prices for:\n${changedItems.slice(0, 3).map(item => `• ${item.part_name}: $${formatNumber(item.adjusted_price)}`).join('\n')}${changedItems.length > 3 ? `\n... and ${changedItems.length - 3} more items` : ''}`;
      
  // if (!window.confirm(confirmMessage)) {
  //   return;
  // }

      setSaving(true);

      // Here you would typically make API calls to save all changes
      // const promises = changedItems.map(item => 
      //   apiService.updateAdjustedPrice(item.id, item.adjusted_price)
      // );
      // await Promise.all(promises);

      // Simulate API delay for demo purposes
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Prepare batchData: only ipe_id and adjusted_price
      const batchData = changedItems.map(item => ({
        ipe_id: item.id,
        adjusted_price: item.adjusted_price
      }));

      await apiService.UpdateBatch(batchData);
      setOriginalData([...finalisedData]);
      setHasChanges(false);

      notificationService.showSuccess(`Successfully saved ${changedItems.length} changes`);

      // Log the batchData for debugging (only in development)
      if (process.env.NODE_ENV === 'development') {
        console.log('Saved batchData:', batchData);
      }

    } catch (error) {
      console.error('Error saving all changes:', error);
      notificationService.showError('Failed to save some changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };













  // Monitor changes in finalisedData to update hasChanges state
  useEffect(() => {
    if (originalData.length > 0 && finalisedData.length > 0) {
      const hasUnsaved = finalisedData.some((item, index) => {
        const original = originalData[index];
        return original && original.adjusted_price !== item.adjusted_price;
      });
      setHasChanges(hasUnsaved);
    }
  }, [finalisedData, originalData]);






const totalItems = filteredData.length;
const totalPages = Math.ceil(totalItems / itemsPerPage);
const startIndex = (currentPage - 1) * itemsPerPage;
const endIndex = startIndex + itemsPerPage;
const currentData = filteredData.slice(startIndex, endIndex);



  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (event) => {
    // Note: itemsPerPage is currently fixed at 10
    // To enable dynamic items per page, uncomment the line below and change the state declaration
    // setItemsPerPage(parseInt(event.target.value));
    setCurrentPage(1);
  };





  // Render pagination controls (matching All Interchanges style)
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        ←
      </button>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key="1"
          className="pagination-btn"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          className="pagination-btn"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        →
      </button>
    );

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          <div className="d-flex align-items-center">
            <span>Show:</span>
            <span className="mx-2 fw-bold">{itemsPerPage}</span>
            <span>entries</span>
          </div>
          <span className="pagination-info-text">
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
          </span>
        </div>
        <div className="pagination-controls">
          {pages}
        </div>
      </div>
    );
  };








  if (loading) {
    return (
      <div style={{
        position: 'fixed',   
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(255,255,255,0.8)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div className="bouncing-loader">
          <div></div><div></div><div></div>
        </div>
        <style>
          {`
          .bouncing-loader {
            display: flex;
            align-items: flex-end;
            justify-content: center;
            height: 60px;
            gap: 10px;
          }
          .bouncing-loader div {
            width: 18px;
            height: 18px;
            background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
            border-radius: 50%;
            animation: bouncing-loader 0.6s infinite alternate;
            box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
          }
          .bouncing-loader div:nth-child(2) {
            animation-delay: 0.2s;
          }
          .bouncing-loader div:nth-child(3) {
            animation-delay: 0.4s;
          }
          @keyframes bouncing-loader {
            to {
              transform: translateY(-30px) scale(1.1);
              box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
            }
          }
          `}
        </style>
      </div>
    );
  }




  
  return (
    <div className="">
         <div className="custom_filter_section filter_section d-flex align-items-start row mb_25 ">

        <div className="filter_items col-md-3">
          <label className="font_14 fw-med text_black">
            Part Type
            {!selectedPartTypes.includes('All') && selectedPartTypes.length > 0 && (
              <span className="badge bg-primary ms-2" style={{ fontSize: '10px' }}>
                {selectedPartTypes.length} selected
              </span>
            )}
          </label>
          <Multiselect
            isObject={false}
            options={partTypes}
            selectedValues={selectedPartTypes}
            onSelect={(selectedList, selectedItem) => {
              handleSelect(selectedList, selectedItem, setSelectedPartTypes);
            }}
            onRemove={(selectedList, removedItem) => {
              handleRemove(selectedList, removedItem, setSelectedPartTypes, partTypes);
            }}
            placeholder="Select Part Type"
          />
        </div> 

        <div className="filter_items col-md-3">
          <label className="font_14 fw-med text_black">
            Regions
            {!selectedRegions.includes('All') && selectedRegions.length > 0 && (
              <span className="badge bg-primary ms-2" style={{ fontSize: '10px' }}>
                {selectedRegions.length} selected
              </span>
            )}
          </label>
                       <Multiselect
             isObject={false}
             options={regions}
             selectedValues={selectedRegions}
             onSelect={(selectedList, selectedItem) => {
               handleSelect(selectedList, selectedItem, setSelectedRegions);
             }}
             onRemove={(selectedList, removedItem) => {
               handleRemove(selectedList, removedItem, setSelectedRegions, regions);
             }}
             placeholder="Select Regions"
           />
        </div>
         
        <div className="filter_items col-md-3">
          <label className="font_14 fw-med text_black">
            IC Number
            {!selectedInterchangeNumbers.includes('All') && selectedInterchangeNumbers.length > 0 && (
              <span className="badge bg-primary ms-2" style={{ fontSize: '10px' }}>
                {selectedInterchangeNumbers.length} selected
              </span>
            )}
          </label>
          <Multiselect
            isObject={false}
            options={interchangeNumbers}
            selectedValues={selectedInterchangeNumbers}
            onSelect={(selectedList, selectedItem) => {
              handleSelect(selectedList, selectedItem, setSelectedInterchangeNumbers);
            }}
            onRemove={(selectedList, removedItem) => {
              handleRemove(selectedList, removedItem, setSelectedInterchangeNumbers, interchangeNumbers);
            }}
            placeholder="Select IC Number"
          />
        </div>
     {/* <div className='filter_items col-md-3'>
          <label className='font_14 fw-med text_black'>Comparision</label>
        
        <div className=" multiselect-container multiSelectContainer d-flex align-items-center search-wrapper searchWrapper comparision_blk">
          <select id="comparison" name="comparison" className='searchBox select_custom'>
            <option value="greater">Greater Than</option>
            <option value="less">Less Than</option>
          </select>
          <input type="text" className='searchBox input_custom w-100px'></input>
        </div> 
        </div>  */}
        
        <div className='filter_items col-md-3'>
  <label className='font_14 fw-med text_black'>% difference</label>

  <div className="d-flex flex-column gap-2">
    {/* Dropdown for Greater / Less with arrow */}
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <select
        id="comparison"
        name="comparison"
        className='form-control'
        value={comparisonOperator}
        onChange={(e) => setComparisonOperator(e.target.value)}
        style={{ 
          padding: '8px 12px',
          paddingRight: '30px',
          fontSize: '14px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none'
        }}
      >
        <option value="greater">Greater Than</option>
        <option value="less">Less Than</option>
      </select>
      <img 
        src={dropdownArrow} 
        alt="dropdown arrow" 
        style={{ 
          position: 'absolute', 
          right: '10px', 
          width: '12px', 
          height: '12px',
          pointerEvents: 'none'
        }} 
      />
    </div>

    {/* Percentage input with % symbol */}
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '120px' }}>
      <input
        type="number"
        step="0.01"
        className='form-control'
        value={comparisonValue}
        onChange={(e) => setComparisonValue(e.target.value)}
        placeholder="0.2"
        style={{ 
          paddingRight: '25px',
          fontSize: '14px',
          padding: '8px 12px',
          width: '100%'
        }}
      />
      <span style={{ 
        position: 'absolute', 
        right: '10px', 
        fontSize: '14px', 
        color: '#666',
        pointerEvents: 'none',
        fontWeight: 'bold'
      }}>%</span>
    </div>
  </div>
</div>

        <div className="filter_button_blk mt_26">
           <img 
             src={searchIcon} 
             alt="search" 
             className="" 
             onClick={handleSearch}
             style={{ 
               cursor: 'pointer',
               transition: 'transform 0.2s'
             }}
             onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
             onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
           />
         </div>
      </div> 



<div className="d-flex justify-content-between align-items-end mb_15">
          <h4 className="font_18 fw-semi mb_0">Finalized Data</h4>
          <div className="d-flex align-items-center gap-3">
            <button 
              className="btn btn-success d-flex align-items-center saving_btn"
              onClick={handleSaveAllChanges}
              disabled={!hasUnsavedChanges() || saving}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                borderRadius: '4px'
              }}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <>
                  💾 Save All Changes
                  {hasUnsavedChanges() && (
                    <span className="badge bg-warning text-dark ms-2">
                      {getUnsavedChangesCount()}
                    </span>
                  )}
                </>
              )}
            </button>
            <button 
              className="btn btn-outline-primary d-flex align-items-center"
              onClick={handleDownloadExcel}
              style={{ 
                border: '1px solid #ff6600', 
                borderRadius: '4px', 
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                color: 'black'
              }}
              title="Export to Excel"
            >
              <img 
                src={downloadIcon} 
                alt="Download" 
                style={{ width: '16px', height: '16px', marginRight: '8px' }}
              />
              Export Report
            </button>
          </div>
        </div>

      <div className="table-section p_15 report_table_bg impact_data_blk">
        
        
        <table className="w-100 report_table impact_analysis_table">
          <thead>
            <tr>
              <th>Part Name</th>
              <th>Region</th>
              <th>IC Number</th>
              <th>Mileage</th>
              <th>Original Price</th>
              <th>Adjusted Price</th>
              <th>% difference</th>
              <th>Finalized By</th>
            </tr>
          </thead>
          <tbody>
             {currentData.map((item, index) => {
               // Calculate percentage difference
               const originalPrice = parseFloat(item.original_price) || 0;
               const adjustedPrice = parseFloat(item.adjusted_price) || 0;
               
               const percentageDifference = originalPrice > 0 
                 ? (((adjustedPrice - originalPrice) / originalPrice) * 100).toFixed(2)
                 : '0.00';
               
               // Determine arrow and color based on percentage difference
               const percentageValue = parseFloat(percentageDifference);
               let arrowIcon = '';
               let arrowColor = '';
               
               if (percentageValue > 0) {
                 arrowIcon = '▲'; // Up arrow for increase
                 arrowColor = '#28a745'; // Green color for arrow
               } else if (percentageValue < 0) {
                 arrowIcon = '▼'; // Down arrow for decrease
                 arrowColor = '#dc3545'; // Red color for arrow
               } else {
                 arrowIcon = '—'; // Minus sign for no change
                 arrowColor = '#6c757d'; // Gray color for arrow
               }
               
               return (
                 <tr key={item.id || index}>
                   <td>{item.part_name}</td>
                   <td>{item.region}</td>
                   <td>{item.interchange_number}</td>
                   <td>{formatNumber(item.mileage)}</td>
                   <td>{`${formatNumber(item.original_price)} $`}</td>
                   <td>
                     <div className="editable-price-container">
                       <input
                         type="number"
                         className="editable-price-input"
                         defaultValue={item.adjusted_price}
                         onBlur={(e) => handlePriceChange(item.id, e.target.value)}
                         onKeyPress={(e) => {
                           if (e.key === 'Enter') {
                             e.target.blur(); // Trigger onBlur to save
                           }
                         }}
                         style={{
                           width: '80px',
                           padding: '4px 8px',
                           border: '1px solid #ddd',
                           borderRadius: '4px',
                           fontSize: '14px'
                         }}
                       />
                       <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>$</span>
                     </div>
                   </td>
                   <td>
                     <span style={{ 
                       color: arrowColor,
                       fontSize: '14px',
                       fontWeight: 'bold',
                       marginRight: '8px'
                     }}>
                       {arrowIcon}
                     </span>
                     <span style={{ color: '#000' }}>
                       {percentageDifference}%
                     </span>
                   </td>
                   <td>{item.approved_by_name}</td>
                 </tr>
               );
             })}
           </tbody>
        </table>
        
        {/* Pagination Controls */}
        {renderPagination()}
      </div>
    </div>
  );
};

export default NewCom;
